import { useState } from 'react';
import type { DataMappingResponse } from '@erp/rpc';
import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { formatDistanceToNow } from 'date-fns';
import { PlusIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { TabsBarActions } from 'src/components/ui/tabs';
import { Badge } from 'src/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { useToast } from 'src/context/ToastContext';
import {
  useListDataMappings,
  useDeleteDataMapping,
} from 'src/api/rpc/hooks/data-mappings/useDataMappings';
import { QueryStateGate } from '../components/QueryStateGate';
import { DataMappingWizard } from './DataMappingWizard';

/**
 * List, create, inspect and delete data mappings. Rendered by both surfaces
 * that manage them — the Workflows tab and the Organization Settings section —
 * so the two never drift.
 */
export function DataMappingsManager({
  /** Put the create button in the enclosing tabs row instead of above the list. */
  inTabsBar = false,
}: {
  inTabsBar?: boolean;
} = {}) {
  const gt = useGT();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data, isLoading, error } = useListDataMappings();
  const deleteMutation = useDeleteDataMapping();
  const [wizardOpen, setWizardOpen] = useState(false);
  /**
   * The mapping a delete is pending on. Business logic resolves mappings by
   * slug, so deleting one silently breaks every workflow that reads it — worth a
   * confirmation, and there is no undo.
   */
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );

  const mappings = data?.dataMappings ?? [];

  function openMapping(dataMappingId: string) {
    navigate({
      to: '/workflows/data-mappings/$dataMappingId',
      params: { dataMappingId },
    });
  }

  async function handleDelete(id: string, name: string) {
    setPendingDelete(null);
    try {
      await deleteMutation.mutateAsync(id);
      showToast({
        title: gt('Deleted {name}', { name }),
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: err instanceof Error ? err.message : gt('Could not delete'),
        color: 'error',
      });
    }
  }

  const createButton = (
    <Button size="sm" onClick={() => setWizardOpen(true)}>
      <PlusIcon className="h-3.5 w-3.5" />
      <T context="button to start the create-data-mapping wizard">
        New Mapping
      </T>
    </Button>
  );

  return (
    <QueryStateGate
      isLoading={isLoading}
      error={error}
      errorMessage={gt('Failed to load data mappings', {
        context: 'data mappings load error',
      })}
    >
      <div className="space-y-2">
        {inTabsBar ? (
          <TabsBarActions>{createButton}</TabsBarActions>
        ) : (
          <div className="flex justify-end">{createButton}</div>
        )}

        <DataMappingsTable
          mappings={mappings}
          onOpen={openMapping}
          onRequestDelete={setPendingDelete}
        />

        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogContent className="max-w-5xl sm:max-w-5xl p-0 overflow-hidden">
            <DialogHeader className="px-5 pt-5">
              <DialogTitle>
                <T context="dialog title for the create-data-mapping wizard">
                  New Data Mapping
                </T>
              </DialogTitle>
            </DialogHeader>
            {wizardOpen && (
              <DataMappingWizard
                onCancel={() => setWizardOpen(false)}
                onCreated={(id) => {
                  setWizardOpen(false);
                  openMapping(id);
                  showToast({
                    title: gt('Data mapping created'),
                    color: 'success',
                  });
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        <DeleteDataMappingDialog
          pending={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDelete}
        />
      </div>
    </QueryStateGate>
  );
}

interface PendingDelete {
  id: string;
  name: string;
  slug: string;
}

function DataMappingsTable({
  mappings,
  onOpen,
  onRequestDelete,
}: {
  mappings: DataMappingResponse[];
  onOpen: (id: string) => void;
  onRequestDelete: (pending: PendingDelete) => void;
}) {
  if (mappings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">
          <T context="empty state when an org has no data mappings">
            No data mappings yet.
          </T>
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <T context="column header for data mapping name">Name</T>
          </TableHead>
          <TableHead>
            <T context="column header for the code-facing slug of a data mapping">
              Slug
            </T>
          </TableHead>
          <TableHead>
            <T context="column header for a data mapping's source file">
              Source
            </T>
          </TableHead>
          <TableHead>
            <T context="column header for when a data mapping last synced">
              Last sync
            </T>
          </TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {mappings.map((mapping) => (
          <DataMappingRow
            key={mapping.id}
            mapping={mapping}
            onOpen={onOpen}
            onRequestDelete={onRequestDelete}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function DataMappingRow({
  mapping,
  onOpen,
  onRequestDelete,
}: {
  mapping: DataMappingResponse;
  onOpen: (id: string) => void;
  onRequestDelete: (pending: PendingDelete) => void;
}) {
  return (
    <TableRow className="cursor-pointer" onClick={() => onOpen(mapping.id)}>
      <TableCell className="font-medium">
        {mapping.name}
        {!mapping.isActive && (
          <Badge variant="outline" className="ml-2">
            <T context="badge marking a data mapping as inactive">Inactive</T>
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <code className="text-xs">{mapping.slug}</code>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {mapping.dataAsset?.name ?? '—'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {mapping.sourceSyncedAt
          ? formatDistanceToNow(new Date(mapping.sourceSyncedAt), {
              addSuffix: true,
            })
          : '—'}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            onRequestDelete({
              id: mapping.id,
              name: mapping.name,
              slug: mapping.slug,
            });
          }}
        >
          <T context="button to delete a data mapping">Delete</T>
        </Button>
      </TableCell>
    </TableRow>
  );
}

/**
 * Business logic resolves mappings by slug, so a deletion silently breaks every
 * workflow reading that slug — and there is no undo.
 */
function DeleteDataMappingDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: PendingDelete | null;
  onCancel: () => void;
  onConfirm: (id: string, name: string) => void;
}) {
  const gt = useGT();

  return (
    <AlertDialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="confirmation title for deleting a data mapping">
              Delete this data mapping?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {gt(
              'Anything resolving "{slug}" stops finding it. The stateful table and its rows are left alone, and this cannot be undone.',
              { slug: pending?.slug ?? '' },
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <T context="button to cancel deleting a data mapping">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (pending) onConfirm(pending.id, pending.name);
            }}
          >
            <T context="button confirming deletion of a data mapping">Delete</T>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
